import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PlusIcon, EyeIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Add/Edit Project
const ProjectModal = ({ isOpen, onClose, onSubmit, initialData, isEdit, countries }) => {
    const [formData, setFormData] = useState(
        initialData || {
            name_ar: "",
            name_en: "",
            country_id: "",
            city_ar: "",
            city_en: "",
            price: "",
            location_link: "",
            area: "",
            rooms_count: "",
            main_image: null,
            gallery_images: [],
            overview: [{ title_ar: "", title_en: "", details_ar: "", details_en: "" }],
            description_ar: "",
            description_en: "",
        }
    );
    const [errors, setErrors] = useState({});
    const [mainImagePreview, setMainImagePreview] = useState(
        isEdit && initialData?.main_image_path
            ? `https://ligasos-laravel.ligasos.co/storage/app/public/${initialData.main_image_path}`
            : null
    );
    const [galleryImagePreviews, setGalleryImagePreviews] = useState(
        isEdit && initialData?.gallery_image_paths
            ? initialData.gallery_image_paths.map(
                  (path) => `https://ligasos-laravel.ligasos.co/storage/app/public/${path}`
              )
            : []
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "main_image" && files[0]) {
            setFormData({ ...formData, main_image: files[0] });
            setMainImagePreview(URL.createObjectURL(files[0]));
        } else if (name === "gallery_images" && files) {
            setFormData({ ...formData, gallery_images: Array.from(files) });
            setGalleryImagePreviews(Array.from(files).map((file) => URL.createObjectURL(file)));
        } else {
            setFormData({ ...formData, [name]: value });
        }
        setErrors({ ...errors, [name]: "" });
    };

    const handleOverviewChange = (index, e) => {
        const { name, value } = e.target;
        const updatedOverview = [...formData.overview];
        updatedOverview[index] = { ...updatedOverview[index], [name]: value };
        setFormData({ ...formData, overview: updatedOverview });
        setErrors({ ...errors, [`overview[${index}].${name}`]: "" });
    };

    const addOverviewField = () => {
        setFormData({
            ...formData,
            overview: [...formData.overview, { title_ar: "", title_en: "", details_ar: "", details_en: "" }],
        });
    };

    const removeOverviewField = (index) => {
        setFormData({
            ...formData,
            overview: formData.overview.filter((_, i) => i !== index),
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name_ar) newErrors.name_ar = "Arabic Name is required";
        if (!formData.name_en) newErrors.name_en = "English Name is required";
        if (!formData.country_id) newErrors.country_id = "Country is required";
        if (formData.main_image && !["image/jpeg", "image/png", "image/webp"].includes(formData.main_image.type)) {
            newErrors.main_image = "Main image must be a JPG, PNG, or WebP file";
        }
        if (formData.gallery_images) {
            formData.gallery_images.forEach((image, index) => {
                if (!["image/jpeg", "image/png", "image/webp"].includes(image.type)) {
                    newErrors[`gallery_images[${index}]`] = "Gallery image must be a JPG, PNG, or WebP file";
                }
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            const data = new FormData();
            data.append("name_ar", formData.name_ar);
            data.append("name_en", formData.name_en);
            data.append("country_id", formData.country_id);
            data.append("city_ar", formData.city_ar || "");
            data.append("city_en", formData.city_en || "");
            data.append("price", formData.price || "");
            data.append("location_link", formData.location_link || "");
            data.append("area", formData.area || "");
            data.append("rooms_count", formData.rooms_count || "");
            if (formData.main_image) data.append("main_image", formData.main_image);
            formData.gallery_images.forEach((image, index) => {
                data.append(`gallery_images[${index}]`, image);
            });
            data.append("overview", JSON.stringify(formData.overview));
            data.append("description_ar", formData.description_ar || "");
            data.append("description_en", formData.description_en || "");
            await onSubmit(data);
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="custom-container">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Project" : "Add Project"}</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white">Name (Arabic)</label>
                        <input
                            type="text"
                            name="name_ar"
                            value={formData.name_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter Arabic name"
                        />
                        {errors.name_ar && <p className="text-red-400 text-sm mt-1">{errors.name_ar}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Name (English)</label>
                        <input
                            type="text"
                            name="name_en"
                            value={formData.name_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter English name"
                        />
                        {errors.name_en && <p className="text-red-400 text-sm mt-1">{errors.name_en}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Country</label>
                        <select
                            name="country_id"
                            value={formData.country_id}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 disabled:opacity-50"
                        >
                            <option value="">Select a country</option>
                            {countries.map((country) => (
                                <option key={country.id} value={country.id}>
                                    {country.name_en}
                                </option>
                            ))}
                        </select>
                        {errors.country_id && <p className="text-red-400 text-sm mt-1">{errors.country_id}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">City (Arabic)</label>
                        <input
                            type="text"
                            name="city_ar"
                            value={formData.city_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter Arabic city (optional)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">City (English)</label>
                        <input
                            type="text"
                            name="city_en"
                            value={formData.city_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter English city (optional)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Price</label>
                        <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter price (optional)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Location Link</label>
                        <input
                            type="text"
                            name="location_link"
                            value={formData.location_link}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter location link (optional)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Area (m²)</label>
                        <input
                            type="number"
                            name="area"
                            value={formData.area}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter area (optional)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Rooms Count</label>
                        <input
                            type="number"
                            name="rooms_count"
                            value={formData.rooms_count}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter rooms count (optional)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Main Image (optional)</label>
                        <input
                            type="file"
                            name="main_image"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#B39359] file:text-white hover:file:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50"
                        />
                        {errors.main_image && <p className="text-red-400 text-sm mt-1">{errors.main_image}</p>}
                        {mainImagePreview && (
                            <img
                                src={mainImagePreview}
                                alt="Main Preview"
                                className="mt-3 w-20 h-20 rounded-full object-cover border-2 border-[#B39359]"
                            />
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Gallery Images (optional)</label>
                        <input
                            type="file"
                            name="gallery_images"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#B39359] file:text-white hover:file:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50"
                        />
                        {errors["gallery_images[0]"] && (
                            <p className="text-red-400 text-sm mt-1">{errors["gallery_images[0]"]}</p>
                        )}
                        {galleryImagePreviews.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {galleryImagePreviews.map((preview, index) => (
                                    <img
                                        key={index}
                                        src={preview}
                                        alt={`Gallery Preview ${index}`}
                                        className="w-16 h-16 rounded-md object-cover border-2 border-[#B39359]"
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Overview (optional)</label>
                        {formData.overview.map((ov, index) => (
                            <div key={index} className="mt-2 p-4 bg-[#1A4A5B] rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-white">Overview Item {index + 1}</h4>
                                    {formData.overview.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeOverviewField(index)}
                                            className="text-red-400 hover:text-red-600"
                                        >
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-white">Title (Arabic)</label>
                                        <input
                                            type="text"
                                            name="title_ar"
                                            value={ov.title_ar}
                                            onChange={(e) => handleOverviewChange(index, e)}
                                            disabled={isSubmitting}
                                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                                            placeholder="Enter Arabic title (optional)"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-white">Title (English)</label>
                                        <input
                                            type="text"
                                            name="title_en"
                                            value={ov.title_en}
                                            onChange={(e) => handleOverviewChange(index, e)}
                                            disabled={isSubmitting}
                                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                                            placeholder="Enter English title (optional)"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-white">Details (Arabic)</label>
                                        <textarea
                                            name="details_ar"
                                            value={ov.details_ar}
                                            onChange={(e) => handleOverviewChange(index, e)}
                                            disabled={isSubmitting}
                                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50 min-h-[100px]"
                                            placeholder="Enter Arabic details (optional)"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-white">Details (English)</label>
                                        <textarea
                                            name="details_en"
                                            value={ov.details_en}
                                            onChange={(e) => handleOverviewChange(index, e)}
                                            disabled={isSubmitting}
                                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50 min-h-[100px]"
                                            placeholder="Enter English details (optional)"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addOverviewField}
                            className="mt-2 text-[#B39359] hover:text-[#D4A373] flex items-center gap-2"
                            disabled={isSubmitting}
                        >
                            <PlusIcon className="w-5 h-5" /> Add Overview Item
                        </button>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Description (Arabic)</label>
                        <textarea
                            name="description_ar"
                            value={formData.description_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50 min-h-[100px]"
                            placeholder="Enter Arabic description (optional)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Description (English)</label>
                        <textarea
                            name="description_en"
                            value={formData.description_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50 min-h-[100px]"
                            placeholder="Enter English description (optional)"
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-[#B39359] text-white hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>{isEdit ? "Update" : "Add"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Modal Component for Viewing Project Details
const ProjectViewModal = ({ isOpen, onClose, project }) => {
    if (!isOpen || !project) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="custom-container">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">Project Details</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-white">Name (Arabic)</label>
                        <p className="mt-1 text-white">{project.name_ar || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Name (English)</label>
                        <p className="mt-1 text-white">{project.name_en || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Country</label>
                        <p className="mt-1 text-white">{project.country?.name_en || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Country Projects Count</label>
                        <p className="mt-1 text-white">{project.country_projects_count || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">City (Arabic)</label>
                        <p className="mt-1 text-white">{project.city_ar || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">City (English)</label>
                        <p className="mt-1 text-white">{project.city_en || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Price</label>
                        <p className="mt-1 text-white">{project.price ? `$${project.price}` : "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Location Link</label>
                        <p className="mt-1 text-white">
                            {project.location_link ? (
                                <a href={project.location_link} target="_blank" rel="noopener noreferrer" className="text-[#B39359] hover:underline">
                                    View Location
                                </a>
                            ) : (
                                "N/A"
                            )}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Area (m²)</label>
                        <p className="mt-1 text-white">{project.area ? `${project.area} m²` : "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Rooms Count</label>
                        <p className="mt-1 text-white">{project.rooms_count || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Main Image</label>
                        {project.main_image_path ? (
                            <img
                                src={`https://ligasos-laravel.ligasos.co/storage/app/public/${project.main_image_path}`}
                                alt={project.name_en}
                                className="mt-3 w-32 h-32 object-cover border-2 border-[#B39359]"
                                onError={(e) => (e.target.src = "https://via.placeholder.com/128")}
                            />
                        ) : (
                            <p className="mt-1 text-white">No Image</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Gallery Images</label>
                        {project.gallery_image_paths && project.gallery_image_paths.length > 0 ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {project.gallery_image_paths.map((image, index) => (
                                    <img
                                        key={index}
                                        src={`https://ligasos-laravel.ligasos.co/storage/app/public/${image}`}
                                        alt={`GalleryImage ${index}`}
                                        className="w-16 h-16 rounded-md object-cover border-2 border-[#B39359]"
                                        onError={(e) => (e.target.src = "https://via.placeholder.com/64")}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="mt-1 text-white">No Gallery Images</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Overview</label>
                        {project.overview && project.overview.length > 0 ? (
                            <div className="mt-3 space-y-3">
                                {project.overview.map((ov, index) => (
                                    <div key={index} className="p-4 bg-[#1A4A5B] rounded-lg">
                                        <h4 className="text-white font-semibold">Item {index + 1}</h4>
                                        <p className="text-white"><strong>Title (Arabic):</strong> {ov.title_ar || "N/A"}</p>
                                        <p className="text-white"><strong>Title (English):</strong> {ov.title_en || "N/A"}</p>
                                        <p className="text-white"><strong>Details (Arabic):</strong> {ov.details_ar || "N/A"}</p>
                                        <p className="text-white"><strong>Details (English):</strong> {ov.details_en || "N/A"}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="mt-1 text-white">No Overview</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Description (Arabic)</label>
                        <p className="mt-1 text-white">{project.description_ar || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Description (English)</label>
                        <p className="mt-1 text-white">{project.description_en || "N/A"}</p>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [countries, setCountries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editProject, setEditProject] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all projects and countries
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [projectsResponse, countriesResponse] = await Promise.all([
                    axios.get("https://ligasos-laravel.ligasos.co/api/projects", {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    axios.get("https://ligasos-laravel.ligasos.co/api/countries", {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                ]);
                setProjects(Array.isArray(projectsResponse.data) ? projectsResponse.data : []);
                setCountries(Array.isArray(countriesResponse.data) ? countriesResponse.data : []);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to fetch data. Please try again.",
                    confirmButtonColor: "#B39359",
                });
                setProjects([]);
                setCountries([]);
            } finally {
                setIsLoading(false);
            }
        };
        if (token) {
            fetchData();
        } else {
            MySwal.fire({
                icon: "error",
                title: "Authentication Error",
                text: "No token found. Please log in.",
                confirmButtonColor: "#B39359",
            });
            setProjects([]);
            setCountries([]);
            setIsLoading(false);
        }
    }, [token]);

    // Handle Add Project
    const handleAddProject = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                "https://ligasos-laravel.ligasos.co/api/projects",
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setProjects([...projects, response.data.data]);
            setIsAddModalOpen(false);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Project added successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to add project.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Edit Project
    const handleEditProject = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `https://ligasos-laravel.ligasos.co/api/projects/${editProject.id}?_method=PUT`,
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setProjects(projects.map((p) => (p.id === editProject.id ? response.data.data : p)));
            setIsEditModalOpen(false);
            setEditProject(null);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Project updated successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to update project.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Delete Project
    const handleDeleteProject = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(`https://ligasos-laravel.ligasos.co/api/projects/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setProjects(projects.filter((p) => p.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Project deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete project.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Handle View Project
    const handleViewProject = async (id) => {
        setIsLoading(true);
        try {
            const response = await axios.get(`https://ligasos-laravel.ligasos.co/api/projects/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedProject(response.data);
            setIsViewModalOpen(true);
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to fetch project details.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Projects</h2>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        className="flex items-center gap-2 bg-[#B39359] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Project
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : projects.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No projects found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Image</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Name (English)</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Country</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Projects Count</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {projects.map((project, index) => (
                                    <tr
                                        key={project.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4">
                                            {project.main_image_path ? (
                                                <img
                                                    src={`https://ligasos-laravel.ligasos.co/storage/app/public/${project.main_image_path}`}
                                                    alt={project.name_en}
                                                    className="w-12 h-12 object-cover border-2 border-[#B39359]"
                                                    onError={(e) => (e.target.src = "https://via.placeholder.com/40")}
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">
                                                    No Image
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{project.name_en || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{project.country?.name_en || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{project.country_projects_count || "N/A"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewProject(project.id)}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <EyeIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditProject(project);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteProject(project.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modals */}
            <ProjectModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddProject}
                isEdit={false}
                countries={countries}
            />
            <ProjectModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditProject(null);
                }}
                onSubmit={handleEditProject}
                initialData={editProject}
                isEdit={true}
                countries={countries}
            />
            <ProjectViewModal
                isOpen={isViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setSelectedProject(null);
                }}
                project={selectedProject}
            />
        </section>
    );
}